import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Menu, Shield, LogOut, Languages } from "lucide-react";
import { useState } from "react";

export default function NavBar() {
  const { user, logout } = useAuth();
  const { t, lang, toggle } = useI18n();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const links = [
    { to: "/", label: t("nav.search"), testId: "nav-search" },
    { to: "/facilities", label: t("nav.facilities"), testId: "nav-facilities" },
    { to: "/map", label: t("nav.map"), testId: "nav-map" },
    { to: "/jurisdictions", label: "Jurisdictions", testId: "nav-jurisdictions" },
    { to: "/tracker", label: "Track · $2/mo", testId: "nav-tracker", highlight: true },
    { to: "/traveler-alert", label: t("nav.alert"), testId: "nav-traveler-alert" },
  ];

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur-sm">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <div className="grid h-9 w-9 place-items-center rounded-md bg-slate-900 text-white">
              <Shield size={18} />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-bold font-heading text-slate-900">US Prisoner &amp; Institution Status</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Public information portal</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    l.highlight
                      ? isActive
                        ? "bg-amber-500 text-white"
                        : "bg-amber-100 text-amber-900 hover:bg-amber-200"
                      : isActive
                      ? "bg-slate-900 text-white"
                      : "text-slate-700 hover:bg-slate-100"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <button
              data-testid="nav-lang-toggle"
              onClick={toggle}
              className="ml-1 inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
              title={t("nav.langToggle")}
            >
              <Languages size={14} /> {lang === "en" ? "ES" : "EN"}
            </button>
            {user?.role === "admin" ? (
              <>
                <NavLink
                  to="/admin/dashboard"
                  data-testid="nav-admin-dashboard"
                  className={({ isActive }) =>
                    `rounded-md px-3 py-2 text-sm font-medium ${isActive ? "bg-blue-600 text-white" : "text-blue-700 hover:bg-blue-50"}`
                  }
                >
                  {t("nav.admin")}
                </NavLink>
                <Button
                  data-testid="nav-logout-btn"
                  variant="outline"
                  size="sm"
                  className="ml-2"
                  onClick={async () => { await logout(); navigate("/"); }}
                >
                  <LogOut size={14} className="mr-1" /> {t("nav.logout")}
                </Button>
              </>
            ) : (
              <NavLink
                to="/admin"
                data-testid="nav-admin-login"
                className="ml-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
              >
                {t("nav.adminLogin")}
              </NavLink>
            )}
          </nav>

          <button
            className="md:hidden rounded-md border border-slate-300 p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            <Menu size={18} />
          </button>
        </div>

        {open && (
          <div className="md:hidden pb-3 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.testId}-mobile`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2 text-sm ${isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <button
              data-testid="nav-lang-toggle-mobile"
              onClick={() => { toggle(); setOpen(false); }}
              className="text-left rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
            >
              {t("nav.langToggle")}
            </button>
            {user?.role === "admin" ? (
              <>
                <Link to="/admin/dashboard" onClick={() => setOpen(false)} className="rounded-md px-3 py-2 text-sm text-blue-700 hover:bg-blue-50">{t("nav.admin")}</Link>
                <button onClick={async () => { await logout(); setOpen(false); navigate("/"); }} className="rounded-md px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100">{t("nav.logout")}</button>
              </>
            ) : (
              <Link to="/admin" onClick={() => setOpen(false)} className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700">{t("nav.adminLogin")}</Link>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
